<template>
  <div class="billing-usage-stats">
    <div v-if="notFound" class="alert alert-warning mb-0">{{ $t('billingUsage.notFound') }}</div>

    <div v-else-if="error" class="alert alert-danger d-flex align-items-center justify-content-between mb-0">
      <span>{{ $t('billingUsage.loadError') }}</span>
      <button class="btn btn-sm btn-outline-danger ml-3" @click.prevent="load">{{ $t('billingUsage.retry') }}</button>
    </div>

    <div v-else-if="!loaded" class="text-muted">
      <i class="fa fa-spinner fa-spin mr-2"></i>{{ $t('billingUsage.loading') }}
    </div>

    <div v-else-if="plans.length === 0" class="text-muted">{{ $t('billingUsage.noPlans') }}</div>

    <template v-else>
      <div class="row mb-3">
        <div v-if="plans.length > 1" class="col-12 col-sm-6 col-lg-3 mb-2">
          <label class="mb-1" for="usage-plan">{{ $t('billingUsage.plan') }}</label>
          <select id="usage-plan" v-model="planId" class="form-control form-control-sm" @change="onPlanChange">
            <option v-for="plan in plans" :key="plan.id" :value="plan.id">
              {{ plan.name || ('#' + plan.id) }} ({{ planStatus(plan.status) }})
            </option>
          </select>
        </div>

        <div class="col-12 col-sm-6 col-lg-3 mb-2">
          <label class="mb-1" for="usage-term">{{ $t('billingUsage.term') }}</label>
          <select id="usage-term" v-model="termId" class="form-control form-control-sm" @change="load">
            <option value="">{{ $t('billingUsage.allTerms') }}</option>
            <option v-for="term in terms" :key="term.id" :value="String(term.id)">
              {{ formatDate(term.term_start) }} → {{ term.term_end ? formatDate(term.term_end) : $t('billingUsage.open') }}
            </option>
          </select>
        </div>

        <div class="col-12 col-sm-6 col-lg-3 mb-2">
          <label class="mb-1" for="usage-period">{{ $t('billingUsage.period') }}</label>
          <select id="usage-period" v-model="period" class="form-control form-control-sm" @change="onPeriodChange">
            <option value="all">{{ $t('billingUsage.wholePlan') }}</option>
            <optgroup v-if="planYears.length > 0" :label="$t('billingUsage.planYearGroup')">
              <option v-for="year in planYears" :key="year.n" :value="'plan:' + year.n">
                {{ $t('billingUsage.planYear', { n: year.n }) }} ({{ formatDate(year.from) }} → {{ formatDate(year.to) }})
              </option>
            </optgroup>
            <optgroup :label="$t('billingUsage.calendarYear')">
              <option v-for="year in calendarYears" :key="year" :value="'cal:' + year">{{ year }}</option>
            </optgroup>
            <option value="custom">{{ $t('billingUsage.customRange') }}</option>
          </select>
        </div>

        <template v-if="period === 'custom'">
          <div class="col-6 col-sm-3 col-lg-2 mb-2">
            <label class="mb-1" for="usage-from">{{ $t('billingUsage.from') }}</label>
            <input id="usage-from" v-model="customFrom" type="date" class="form-control form-control-sm" @change="onCustomChange">
          </div>
          <div class="col-6 col-sm-3 col-lg-2 mb-2">
            <label class="mb-1" for="usage-to">{{ $t('billingUsage.to') }}</label>
            <input id="usage-to" v-model="customTo" type="date" class="form-control form-control-sm" @change="onCustomChange">
          </div>
        </template>

        <div v-if="loading" class="col-auto d-flex align-items-end pb-2 text-muted">
          <i class="fa fa-spinner fa-spin"></i>
        </div>
      </div>

      <template v-if="stats">
        <div class="row">
          <div class="col-6 col-md-3">
            <div class="block block-rounded mb-3">
              <div class="block-content block-content-full">
                <div class="text-muted small">{{ $t('billingUsage.played') }}</div>
                <div class="h3 mb-0">{{ number(stats.summary.played) }}</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="block block-rounded mb-3">
              <div class="block-content block-content-full">
                <div class="text-muted small">{{ $t('billingUsage.coveredByPrepaid') }}</div>
                <div class="h3 mb-0">{{ number(stats.summary.covered_by_prepaid) }}</div>
                <div class="text-muted small">{{ $t('billingUsage.prepaidPaid', { amount: money(stats.summary.prepaid_amount) }) }}</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="block block-rounded mb-3">
              <div class="block-content block-content-full">
                <div class="text-muted small">{{ $t('billingUsage.billedInTiers') }}</div>
                <div class="h3 mb-0">{{ number(stats.summary.billed_in_tiers) }}</div>
                <div class="text-muted small">{{ $t('billingUsage.billedAmount', { amount: money(stats.summary.billed_amount) }) }}</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="block block-rounded mb-3">
              <div class="block-content block-content-full">
                <div class="text-muted small">{{ $t('billingUsage.notInvoiced') }}</div>
                <div class="h3 mb-0">{{ number(stats.summary.not_yet_invoiced) }}</div>
                <div class="text-muted small">{{ $t('billingUsage.notInvoicedHint') }}</div>
              </div>
            </div>
          </div>
        </div>

        <h5 class="mt-2 mb-2">{{ $t('billingUsage.lotsTitle') }}</h5>
        <div class="table-responsive mb-4">
          <table class="table table-sm table-striped table-vcenter mb-0">
            <thead>
              <tr>
                <th>{{ $t('billingUsage.lot') }}</th>
                <th>{{ $t('billingUsage.purchased') }}</th>
                <th>{{ $t('billingUsage.expires') }}</th>
                <th>{{ $t('billingUsage.gamesInLot') }}</th>
                <th>{{ $t('billingUsage.usedInPeriod') }}</th>
                <th>{{ $t('billingUsage.usedAllTime') }}</th>
                <th>{{ $t('billingUsage.remaining') }}</th>
                <th>{{ $t('billingUsage.paid') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="lot in stats.lots" :key="lot.id">
                <td>#{{ lot.id }}<span v-if="lot.description" class="text-muted"> · {{ lot.description }}</span></td>
                <td>{{ lot.purchased_at ? formatDate(lot.purchased_at) : '—' }}</td>
                <td>{{ lot.expires_at ? formatDate(lot.expires_at) : $t('billingUsage.never') }}</td>
                <td>{{ number(lot.qty_total) }}</td>
                <td>{{ number(lot.used_in_filter) }}</td>
                <td>{{ number(lot.used_all_time) }}</td>
                <td>{{ number(lot.qty_remaining) }}</td>
                <td>{{ money(lot.amount_paid) }}</td>
              </tr>
              <tr v-if="stats.lots.length === 0">
                <td colspan="8" class="text-center text-muted">{{ $t('billingUsage.noLots') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h5 class="mb-2">{{ $t('billingUsage.tiersTitle') }}</h5>
        <div class="table-responsive mb-4">
          <table class="table table-sm table-striped table-vcenter mb-0">
            <thead>
              <tr>
                <th>{{ $t('billingUsage.tier') }}</th>
                <th>{{ $t('billingUsage.pricePerGame') }}</th>
                <th>{{ $t('billingUsage.billedGames') }}</th>
                <th>{{ $t('billingUsage.amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tier in stats.tiers" :key="tier.tier_from + '-' + tier.tier_to + '-' + tier.unit_price">
                <td>{{ tierLabel(tier) }}</td>
                <td>{{ money(tier.unit_price) }}</td>
                <td>{{ number(tier.quantity) }}</td>
                <td>{{ money(tier.amount) }}</td>
              </tr>
              <tr v-if="stats.tiers.length === 0">
                <td colspan="4" class="text-center text-muted">{{ $t('billingUsage.noTiers') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h5 class="mb-2">{{ $t('billingUsage.monthsTitle') }}</h5>
        <div class="table-responsive mb-3">
          <table class="table table-sm table-striped table-vcenter mb-0">
            <thead>
              <tr>
                <th>{{ $t('billingUsage.month') }}</th>
                <th>{{ $t('billingUsage.played') }}</th>
                <th>{{ $t('billingUsage.coveredByPrepaid') }}</th>
                <th>{{ $t('billingUsage.billedInTiers') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="month in stats.months" :key="month.month">
                <td>{{ monthLabel(month.month) }}</td>
                <td>{{ number(month.played) }}</td>
                <td>{{ number(month.prepaid) }}</td>
                <td>{{ number(month.billed) }}</td>
              </tr>
              <tr v-if="stats.months.length === 0">
                <td colspan="4" class="text-center text-muted">{{ $t('billingUsage.noGames') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="text-muted small mb-0">{{ $t('billingUsage.footnote') }}</p>
      </template>
    </template>
  </div>
</template>

<script>
import BillingService from '@/services/billing.service'

// BCP 47 has no Bosnian code "ba" (it is Bashkir), the app uses it for Bosnian; the Serbian translations are Latin
const INTL_LOCALES = { ba: 'bs', sr: 'sr-Latn' }

export default {
  name: 'BillingUsageStats',
  props: {
    billingProfileId: {
      type: [Number, String],
      required: true
    },
    currency: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      loaded: false,
      loading: false,
      error: false,
      notFound: false,
      plans: [],
      planId: null,
      stats: null,
      // Filters: term and period (whole plan duration, a plan year, a calendar year or a custom range)
      termId: '',
      period: 'all',
      customFrom: '',
      customTo: ''
    }
  },
  computed: {
    intlLocale () {
      const locale = this.$i18n.locale
      return INTL_LOCALES[locale] || locale || undefined
    },
    plan () {
      return this.plans.find(p => p.id === this.planId) || null
    },
    terms () {
      return this.plan ? this.plan.terms : []
    },
    startAt () {
      return this.plan ? this.plan.start_at : null
    },
    calendarYears () {
      const currentYear = new Date().getFullYear()
      const startYear = this.startAt ? new Date(this.startAt).getUTCFullYear() : currentYear
      const first = Number.isNaN(startYear) ? currentYear : startYear
      const years = []
      for (let year = currentYear; year >= first; year--) {
        years.push(year)
      }
      return years
    },
    // Plan years start on the anniversary of the plan start (plan year 1 = first 12 months)
    planYears () {
      if (!this.startAt) return []
      const start = new Date(this.startAt)
      if (Number.isNaN(start.getTime())) return []
      const years = []
      const now = new Date()
      for (let n = 1; n < 200; n++) {
        const from = new Date(Date.UTC(start.getUTCFullYear() + n - 1, start.getUTCMonth(), start.getUTCDate()))
        if (from > now) break
        const to = new Date(Date.UTC(start.getUTCFullYear() + n, start.getUTCMonth(), start.getUTCDate() - 1))
        years.push({ n, from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) })
      }
      return years.reverse()
    },
    range () {
      if (this.period === 'all') return null
      if (this.period === 'custom') return { from: this.customFrom, to: this.customTo }
      if (this.period.startsWith('cal:')) {
        const year = this.period.slice(4)
        return { from: `${year}-01-01`, to: `${year}-12-31` }
      }
      const planYear = this.planYears.find(y => `plan:${y.n}` === this.period)
      return planYear ? { from: planYear.from, to: planYear.to } : null
    }
  },
  created () {
    this.requestId = 0
    this.load()
  },
  methods: {
    load () {
      const requestId = ++this.requestId
      this.loading = true
      this.error = false
      this.notFound = false

      const params = {}
      if (this.planId) params.plan_id = this.planId
      if (this.termId !== '') params.term_id = this.termId
      if (this.range && this.range.from) params.from = this.range.from
      if (this.range && this.range.to) params.to = this.range.to

      return BillingService.usageStats(this.billingProfileId, params)
        .then(res => {
          if (requestId !== this.requestId) return
          const data = res.data || {}
          this.plans = data.plans || []
          this.planId = data.plan_id || null
          this.stats = data.stats || null
          this.loaded = true
        })
        .catch(err => {
          if (requestId !== this.requestId) return
          if (err && err.response && err.response.status === 404) {
            this.notFound = true
          } else {
            this.error = true
          }
        })
        .finally(() => {
          if (requestId === this.requestId) this.loading = false
        })
    },
    onPlanChange () {
      this.termId = ''
      this.period = 'all'
      this.customFrom = ''
      this.customTo = ''
      this.load()
    },
    onPeriodChange () {
      // a custom range is loaded only when both dates are set
      if (this.period === 'custom' && (!this.customFrom || !this.customTo)) return
      this.load()
    },
    onCustomChange () {
      if (this.period === 'custom' && this.customFrom && this.customTo) this.load()
    },
    planStatus (status) {
      const key = `billingUsage.planStatus.${status}`
      return this.$te(key) ? this.$t(key) : status
    },
    tierLabel (tier) {
      return tier.tier_to === null ? `${tier.tier_from || 0}+` : `${tier.tier_from || 0} - ${tier.tier_to}`
    },
    number (value) {
      return new Intl.NumberFormat(this.intlLocale).format(value || 0)
    },
    money (value) {
      const currency = (this.stats && this.stats.currency) || this.currency || 'USD'
      try {
        return new Intl.NumberFormat(this.intlLocale, { style: 'currency', currency }).format(value || 0)
      } catch (e) {
        return `${this.number(value)} ${currency}`
      }
    },
    formatDate (date) {
      return new Date(`${date}T00:00:00Z`).toLocaleDateString(this.intlLocale, { timeZone: 'UTC' })
    },
    monthLabel (month) {
      const [year, m] = month.split('-')
      return new Date(Date.UTC(Number(year), Number(m) - 1, 1)).toLocaleDateString(this.intlLocale, { month: 'short', year: 'numeric', timeZone: 'UTC' })
    }
  }
}
</script>
